import { OptionGroup } from '../../components/molecules/FilterBar';

export interface LabelValue {
  label: string;
  value: string;
  /** Optional override for the dropdown's search-input matching. Lets the
   *  visible label stay clean while keeping hidden metadata (e.g. a client
   *  short code) findable by typing it. */
  searchText?: string;
}

/** Build status filter options from raw status data.
 *  Each active status becomes one option whose `value` is the status `code`
 *  (unique per record). Two statuses that share a display name (e.g. legacy
 *  and current "Examining" codes) produce two distinct options with distinct
 *  values, eliminating the duplicate React-key problem that previously caused
 *  DOM accumulation on re-renders. */
export const buildStatusOptions = (
  data: Array<{ name: string; isActive: boolean; code: string }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data.filter((s) => s.isActive).map((s) => ({ label: s.name, value: s.code }));
};

/** Build status groups (Open / Closed) from raw status data.
 *  Group `values` arrays contain status codes (unique per record), so no
 *  deduplication is required — the GroupHeader "allSelected" check works
 *  correctly because each option has a unique value. */
export const buildStatusGroups = (
  data: Array<{ name: string; isActive: boolean; isClosed?: boolean; code: string }> | undefined
): OptionGroup[] => {
  if (!data) return [];
  const active = data.filter((s) => s.isActive);
  return [
    { name: 'Open', values: active.filter((s) => !s.isClosed).map((s) => s.code) },
    { name: 'Closed', values: active.filter((s) => s.isClosed).map((s) => s.code) },
  ];
};

/** Build job type filter options from raw types data. */
export const buildJobTypeOptions = (
  data: { serviceTypes?: Array<{ name: string; isActive: boolean }> } | undefined
): LabelValue[] => {
  if (!data?.serviceTypes) return [];
  return data.serviceTypes.filter((t) => t.isActive).map((t) => ({ label: t.name, value: t.name }));
};

/**
 * Build contract filter options from contract structure nodes.
 *
 * The BFF matches contracts by *name* (`contractName`), so the option value is
 * the contract name rather than an id — no lookup map is needed on the way to
 * the query. Labels read "CLIENTCODE - Contract Name" so contracts belonging to
 * different clients stay tellable apart, and the client code is repeated in
 * `searchText` so typing it still finds the contract.
 *
 * Options are grouped by name: the dropdown keys its rows by option value, so
 * two contracts sharing a name have to collapse into one row. That merge is
 * faithful to what the filter does — a `contractName` predicate matches every
 * contract with that name regardless. A name shared across clients drops the
 * code prefix (a single code would misrepresent it) and carries every
 * contributing code in `searchText`.
 */
export const buildContractOptions = (
  contracts: Array<{ name: string; isActive: boolean; parentNodeId?: string }> | undefined,
  clients: Array<{ code?: string; id: number }> | undefined
): LabelValue[] => {
  if (!contracts) return [];

  // parentNodeId on a contract is the owning client's numeric id as a string.
  const clientCodeByNodeId: Record<string, string> = {};
  for (const client of clients ?? []) {
    if (client.code) clientCodeByNodeId[String(client.id)] = client.code;
  }

  const clientCodesByContractName = new Map<string, string[]>();
  for (const contract of contracts) {
    if (!contract.isActive) continue;
    const codes = clientCodesByContractName.get(contract.name) ?? [];
    const code = contract.parentNodeId ? clientCodeByNodeId[contract.parentNodeId] : undefined;
    if (code && !codes.includes(code)) codes.push(code);
    clientCodesByContractName.set(contract.name, codes);
  }

  return [...clientCodesByContractName.entries()]
    .map(([name, codes]) => ({
      label: codes.length === 1 ? `${codes[0]} - ${name}` : name,
      value: name,
      searchText: [...codes, name].join(' '),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Build client filter options from client structure nodes.
 *
 * Uses the client `id` (as a string) as the option value so each client is
 * uniquely selectable even when two clients share the same name or short code.
 * The short code is preserved in `searchText` so users can still find a client
 * by typing its code in the dropdown's search box.
 *
 * Selecting a client stores its id in FilterState; `buildFilterModel` converts
 * the id back to `clientName` via the `contractIdToName` map for the BFF query.
 */
export const buildClientOptions = (
  clients: Array<{ name: string; isActive: boolean; code?: string; id: number }> | undefined
): LabelValue[] => {
  if (!clients) return [];
  return clients
    .flatMap((c) => {
      if (!c.isActive) return [];
      return [
        {
          label: c.name,
          value: String(c.id),
          searchText: c.code ? `${c.name} ${c.code}` : c.name,
        },
      ];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};
